import React from "react";
import { Row, Col } from "reactstrap";
import { Drawer, Descriptions, Tag, Empty } from "antd";
import { UserOutlined, CheckCircleOutlined } from "@ant-design/icons";

interface ApprovalData {
  roleId: any;
  level: string;
  userId: any;
  approvalStage?: string;
  roleName?: string;
  userName?: string;
}

interface WorkRequestApprovalViewDrawerProps {
  open: boolean;
  onClose: () => void;
  approvalData: ApprovalData[];
  title?: string;
}

const WorkRequestApprovalViewDrawer: React.FC<WorkRequestApprovalViewDrawerProps> = ({
  open,
  onClose,
  approvalData,
  title = "View Work Request Approvals"
}) => {
  const getStageColor = (stage: string) => {
    switch (stage) {
      case "Approver":
        return "green";
      case "Checker":
        return "blue";
      case "Reviewer":
        return "orange";
      default:
        return "default";
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case "Approver":
        return <CheckCircleOutlined />;
      case "Checker":
      case "Reviewer":
        return <UserOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={onClose}
      open={open}
      width={600}
      styles={{ body: { paddingBottom: 80 } }}
    >
      <div className="approval-hierarchy-view">
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            📋 Approval Hierarchy Configuration
          </h6>
          <p className="text-muted small">
            This is the configured approval hierarchy for this work request. 
            The hierarchy cannot be modified in edit mode.
          </p>
        </div>

        {approvalData && approvalData.length > 0 ? (
          <div className="approval-levels">
            {approvalData
              .sort((a, b) => a.level.localeCompare(b.level)) // Sort by level (L1, L2, L3...)
              .map((approval, index) => (
                <div key={`${approval.level}-${index}`} className="approval-card mb-3">
                  <div
                    className="card border"
                    style={{
                      borderLeft: `4px solid ${
                        approval.approvalStage === "Approver" 
                          ? "#52c41a" 
                          : approval.approvalStage === "Checker" 
                          ? "#1890ff" 
                          : "#fa8c16"
                      }`,
                      backgroundColor: "#fafafa"
                    }}
                  >
                    <div className="card-body p-3">
                      <Row gutter={[16, 8]}>
                        <Col span={24}>
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="approval-level">
                              <Tag color="purple" className="mb-1">
                                {approval.level}
                              </Tag>
                            </div>
                            <div className="approval-stage">
                              <Tag 
                                color={getStageColor(approval.approvalStage || "")}
                                icon={getStageIcon(approval.approvalStage || "")}
                              >
                                {approval.approvalStage}
                              </Tag>
                            </div>
                          </div>
                        </Col>
                        <Col span={24}>
                          <Descriptions bordered size="small" column={1}>
                            <Descriptions.Item label="Role">
                              <strong>{approval.roleName || "N/A"}</strong>
                            </Descriptions.Item>
                            <Descriptions.Item label="Assigned User">
                              <div className="d-flex align-items-center">
                                <UserOutlined className="mr-2" />
                                <strong>{approval.userName || "Not Assigned"}</strong>
                              </div>
                            </Descriptions.Item>
                            <Descriptions.Item label="User ID">
                              <code className="text-muted">
                                {approval.userId || "N/A"}
                              </code>
                            </Descriptions.Item>
                          </Descriptions>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <p>No approval hierarchy configured</p>
                <small className="text-muted">
                  This work request does not have any approval hierarchy set up.
                </small>
              </div>
            }
          />
        )}

        {/* Summary Information */}
        {approvalData && approvalData.length > 0 && (
          <div className="approval-summary mt-4 p-3 bg-light rounded">
            <h6 className="mb-2">📊 Approval Summary</h6>
            <Row gutter={[16, 8]}>
              <Col span={12}>
                <div className="text-center">
                  <div className="h4 mb-1 text-primary">{approvalData.length}</div>
                  <small className="text-muted">Total Levels</small>
                </div>
              </Col>
              <Col span={12}>
                <div className="text-center">
                  <div className="h4 mb-1 text-success">
                    {approvalData.filter(a => a.approvalStage === "Approver").length}
                  </div>
                  <small className="text-muted">Approvers</small>
                </div>
              </Col>
            </Row>
          </div>
        )}

        <div className="mt-4 text-center">
          <p className="text-muted small">
            <strong>Note:</strong> Approval hierarchy is read-only in edit mode. 
            To modify approvals, please create a new work request.
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default WorkRequestApprovalViewDrawer;